import { useFormik } from "formik";
import { Dropdown } from "primereact/dropdown";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { useSnack } from "../../../hooks/useSnack";
import teacherServices from "../../../services/teacherServices";
import Button from "../../Button";
import Modal from "../../Modal";

const DisapproveModal = forwardRef(({ onSubmit }, ref) => {
  const { t } = useTranslation();
  const { showSnack } = useSnack();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [approveDisapproveMessages, setApproveDisapproveMessages] = useState([]);
  const [projectID, setProjectID] = useState(null);

  useImperativeHandle(ref, () => ({
    openModal(projectID) {
      setProjectID(projectID);
      setIsOpen(true);
    },
    closeModal() {
      handleCloseModal();
    },
  }));

  const handleCloseModal = () => {
    setIsOpen(false);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues: {
      comment: "",
      selectedComment: "",
    },
    validationSchema: Yup.object({
      selectedComment: Yup.string().required(t("validation.required")),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const payload = {
          message_id: values.selectedComment,
          description: values.comment,
          status: 4, // Assuming 4 = Disapproved (you may adjust to your actual disapprove status code)
        };
        const response = await teacherServices.approveStudentTask(projectID, payload);
        showSnack(response.message, "success");
        resetForm();
        onSubmit(values);
        setIsOpen(false);
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      } catch (error) {
        setIsSubmitting(false);
        showSnack(error, "error");
      }
    },
  });

  const fetchAprroveDisapproveMessages = async () => {
    try {
      const response = await teacherServices.getApproveDisapproveMessages(2); // 2 for disapprove messages
      const formattedMessages = response.map((message) => ({
        value: message.id,
        label: message.message,
      }));
      setApproveDisapproveMessages(formattedMessages);
    } catch (error) {
      showSnack(error, "error");
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAprroveDisapproveMessages();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      className="w-full max-w-md"
      contentClassname="p-0"
      header={<h2 className="text-center text-2xl font-bold text-gray-900">{t("disapproval_reason")}</h2>}
    >
      <form onSubmit={formik.handleSubmit} className="space-y-4 px-6 py-2">
        <label
          htmlFor="selectedComment"
          className="block text-sm font-semibold text-gray-600 dark:text-white"
        >
          {t("select_disapproval_reason")}
        </label>
        <Dropdown
          pt={{
            wrapper: "text-sm",
            header: "bg-red-100 dark:bg-gray-700 p-2 rounded-t-md",
            input: "p-0 text-sm placeholder:text-gray-500 dark:text-gray-300",
            label: "p-0 text-sm placeholder:text-gray-500 dark:text-gray-300",
            root: "w-full rounded-md border bg-gray-100 px-3 py-2 focus:border-red-500 focus:ring focus:ring-red-500 focus:ring-opacity-10 dark:border-gray-700 dark:bg-gray-800",
            panel:
              "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
            item: "p-2 hover:bg-red-100 dark:hover:bg-red-700 break-words ",
          }}
          value={formik.values.selectedComment}
          onChange={(e) => {
            const selected = e.value;
            formik.setFieldValue("selectedComment", selected);
            const selectedMessage = approveDisapproveMessages.find((msg) => msg.value === selected);
            if (selectedMessage) {
              formik.setFieldValue("comment", selectedMessage.label);
            }
          }}
          options={approveDisapproveMessages}
          placeholder={t("select_comment")}
          filter
          emptyFilterMessage={t("no_results_found")}
          optionLabel="label"
          optionValue="value"
        />
        <label htmlFor="comment" className="block text-sm font-semibold text-gray-600 dark:text-white">
          {t("feel_free_to_leave_a_comment")}
        </label>
        <textarea
          id="comment"
          name="comment"
          rows={4}
          className="w-full rounded-lg border p-3 focus:outline-none focus:ring-2 focus:ring-gray-500"
          value={formik.values.comment}
          onChange={formik.handleChange}
        />

        {formik.errors.selectedComment && formik.touched.selectedComment && (
          <p className="text-sm text-red-500">{formik.errors.selectedComment}</p>
        )}

        <Button
          disabled={isSubmitting}
          loading={isSubmitting}
          type="submit"
          label={t("disapprove")}
          className="w-full rounded-lg bg-red-500 py-3 font-semibold text-white shadow hover:bg-red-600"
        />
      </form>
    </Modal>
  );
});

export default DisapproveModal;
